import * as React from 'react';
import { connect } from 'react-redux';
import * as userService from '../../../services/user.service';
import NotifyHOC from '../../../HOC/Notifications';
import { User, USER_ACCESS_TYPES } from '../../../interface/user.interface';
import * as role from '../../../mapping/roles';
import Select from 'react-select';
import { Formik, Form } from 'formik';
import TextField from '../../common/textField';
import IndicatorsContainer from '../../common/reactSelect/IndicatorsContainer';

import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalTitle
} from '../../common/modal';
import { createUserValidationSchema } from '../../common/validation/validationSchema';
import { alertMessage } from '../../../constants/alertMessage';

interface ICreateUserProps {
    tenantId: number;
    isCreateUser: boolean;
    toastNotification: any;
    user: User;
    toggleCreateUser: () => void;
    getUsers: (tenantId: number) => void;
}

interface ICreateUserState {
    tenantId: number;
    accessType: string;
    selectedOption: any;
    users: User[];
    isRequesting: boolean;
}

class CreateUser extends React.Component<ICreateUserProps, ICreateUserState> {
    constructor(props) {
        super(props);
        this.state = {
            users: [],
            isRequesting: false,
            tenantId: null,
            selectedOption: null,
            accessType: ''
        };
    }

    onClose = () => {
        this.props.toggleCreateUser();
    };

    handleChange = (selectedOption) => {
        this.setState({ selectedOption, accessType: selectedOption.value });
    };

    handleSubmit = async (values: any, { setSubmitting }) => {
        this.createNewUser(values);
        setSubmitting(false);
    };

    createNewUser = async (values) => {
        this.setState({
            isRequesting: true
        });
        const data = {
            ...values,
            inviterName: this.props.user.firstName,
            tenantId: this.props.tenantId,
            accessType: this.state.accessType
        };

        try {
            await userService.inviteUser(data);
            this.setState({
                isRequesting: false
            });
            this.props.toggleCreateUser();
            this.props.toastNotification.success(
                alertMessage.SUCCESS_INVITE_USER
            );
            this.props.getUsers(this.props.tenantId);
        } catch (error) {
            this.setState({
                isRequesting: false
            });
            this.props.toastNotification.error(error.response.data.message);
        }
    };

    render() {
        const { isRequesting, selectedOption } = this.state;

        return (
            <Modal className="modal__heightauto" isOpen={true}>
                <ModalHeader onClose={this.onClose}>
                    <ModalTitle>Add user</ModalTitle>
                </ModalHeader>
                <Formik
                    initialValues={{
                        email: '',
                        firstName: '',
                        lastName: '',
                        company: this.props.user.company || '',
                        jobTitle: ''
                    }}
                    validationSchema={createUserValidationSchema}
                    onSubmit={(values, actions) => {
                        this.handleSubmit(values, actions);
                    }}
                    render={({
                        values,
                        touched,
                        handleChange,
                        handleBlur,
                        errors,
                        isSubmitting
                    }) => (
                        <Form>
                            <ModalBody>
                                <TextField
                                    type="text"
                                    name="email"
                                    value={values.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="Email"
                                    label="Email"
                                    errorMessage={errors.email}
                                    touchedValue={touched.email}
                                    required={true}
                                />
                                <TextField
                                    type="text"
                                    name="firstName"
                                    value={values.firstName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="First Name"
                                    label="First Name"
                                    errorMessage={errors.firstName}
                                    touchedValue={touched.firstName}
                                    required={true}
                                />
                                <TextField
                                    type="text"
                                    name="lastName"
                                    value={values.lastName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="Last Name"
                                    label="Last Name (Optional)"
                                    errorMessage={errors.lastName}
                                    touchedValue={touched.lastName}
                                />
                                <TextField
                                    type="text"
                                    name="company"
                                    value={values.company}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="Company"
                                    label="Company"
                                    errorMessage={errors.company}
                                    touchedValue={touched.company}
                                />
                                <TextField
                                    type="text"
                                    name="jobTitle"
                                    value={values.jobTitle}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="Job Title"
                                    label="Job Title (Optional)"
                                    errorMessage={errors.jobTitle}
                                    touchedValue={touched.jobTitle}
                                />
                                <div className="form-group">
                                    <label className="form-group__label form-group--block">
                                        Account type
                                    </label>
                                    <Select
                                        className="react-select"
                                        classNamePrefix="react-select"
                                        name="form-field-name"
                                        value={selectedOption}
                                        options={role.options}
                                        onChange={this.handleChange}
                                        placehodler="Select access type"
                                        components={{
                                            IndicatorsContainer
                                        }}
                                    />
                                    {this.state.accessType ==
                                        USER_ACCESS_TYPES.PROJECT_MANAGER && (
                                        <span>
                                            Project Managers are not able to
                                            invite new users to the account
                                        </span>
                                    )}
                                </div>
                            </ModalBody>
                            <ModalFooter>
                                <button
                                    disabled={isSubmitting || isRequesting}
                                    className="btn btn--sm btn--transparent m-r-10"
                                    onClick={() =>
                                        this.props.toggleCreateUser()
                                    }
                                >
                                    Cancel
                                </button>
                                <button
                                    disabled={isSubmitting || isRequesting}
                                    className="btn btn--sm btn--blue"
                                    type="submit"
                                >
                                    {isRequesting ? 'Saving user' : 'Save'}
                                </button>
                            </ModalFooter>
                        </Form>
                    )}
                />
            </Modal>
        );
    }
}

const mapStateToProps = (store) => {
    const {
        authReducer: { user }
    } = store;
    return {
        user
    };
};

export default NotifyHOC(connect(mapStateToProps)(CreateUser));
